import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import CloseIcon from '@mui/icons-material/Close';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import KeyboardArrowRightOutlinedIcon from '@mui/icons-material/KeyboardArrowRightOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { Box, TextField, Avatar, Tabs, Tab, Autocomplete, IconButton } from '@mui/material';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import DialogSelectArtworks from 'commons/Collection/CollectionActionBlock/components/DialogSelectArtworks';
import DialogCustom from 'commons/DialogCustom';
import Text from 'commons/Text';
import API from 'configs/API';
import useDebounce from 'hooks/useDebounce';
import useUploadImageS3 from 'hooks/useUploadImageS3';
import { axiosGet } from 'utils/apis/axios';
import { ToastTopHelper } from 'utils/utils';

import useStyles from './styles';

const ShareLinkSettingsDialog = ({ open, onClose, onNext, selectedItems, loading }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const user = useSelector((state) => state.auth.account.user);
  const [searchParams, setSearchParams] = useSearchParams();

  // Form state
  const [expiresAt, setExpiresAt] = useState(null);
  const [title, setTitle] = useState('');
  const [tabValue, setTabValue] = useState('invite');

  const [localSelectedItems, setLocalSelectedItems] = useState(selectedItems);
  const [isOpenArtworkSelect, setIsOpenArtworkSelect] = useState(false);
  const [draftSelectedIds, setDraftSelectedIds] = useState([]);

  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreviewUrl, setBannerPreviewUrl] = useState('');
  const [bannerImageKey, setBannerImageKey] = useState('');
  const bannerInputRef = useRef(null);

  const [inputValue, setInputValue] = useState('');
  const [selectedProfiles, setSelectedProfiles] = useState([]);
  const inputValueDebounce = useDebounce(inputValue, 500);

  const { generateImageKey, uploadImage } = useUploadImageS3();

  // Fetch user options based on search query
  const { data: userOptions = [] } = useQuery({
    queryKey: [API.AUTH.SEARCH_USER, { search: inputValueDebounce, exclude__in: user?.id }],
    enabled: !!inputValueDebounce,
  });

  const handleSelectedChange = (_, value) => {
    setSelectedProfiles(value);
  };

  const objectParamsFromSearchParams = Object.fromEntries(searchParams);

  const params = {
    ...objectParamsFromSearchParams,
    user_uuid: user?.uuid,
  };

  const fetchArtwork = async ({ pageParam }) => {
    const { data } = await axiosGet(false, API.ARTWORK.LIST_ARTWORK_SELECTION, {
      params: {
        ...params,
        page: pageParam,
      },
    });

    return data || { results: [], next: null, previous: null };
  };

  const { data: artworksData, fetchNextPage } = useInfiniteQuery({
    queryKey: [API.ARTWORK.LIST_ARTWORK_SELECTION, params],
    queryFn: fetchArtwork,
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (!lastPage?.next) {
        return null;
      }
      const urlObj = new URL(lastPage?.next);
      const urlParams = new URLSearchParams(urlObj.search);
      return urlParams.get('page');
    },
    getPreviousPageParam: (firstPage) => {
      if (!firstPage?.previous) {
        return null;
      }
      const urlObj = new URL(firstPage?.previous);
      const urlParams = new URLSearchParams(urlObj.search);
      return urlParams.get('page');
    },
    enabled: isOpenArtworkSelect,
    placeholderData: { pages: [], pageParams: [] },
  });

  const allSelectableArtworks = artworksData?.pages?.flatMap((p) => p?.results || []) || [];

  const openSelector = () => {
    const preSelected = (localSelectedItems?.artworks || []).map((a) => a.id);
    setDraftSelectedIds(preSelected);
    setIsOpenArtworkSelect(true);
  };

  const closeSelector = () => {
    setSearchParams({});
    setIsOpenArtworkSelect(false);
  };

  const applySelector = () => {
    const selectedArtworks = draftSelectedIds
      .map((id) => allSelectableArtworks.find((a) => a.id === id))
      .filter(Boolean);
    setLocalSelectedItems((prev) => ({
      ...(prev || {}),
      artworks: selectedArtworks,
      exhibitions: prev?.exhibitions || [],
    }));
    setIsOpenArtworkSelect(false);
    setSearchParams({});
  };

  const handleScroll = (event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.target;

    if (scrollTop + clientHeight >= scrollHeight - 1) {
      fetchNextPage();
    }
  };

  const handleNext = async () => {
    const selectedArtworkIds = (localSelectedItems?.artworks || []).map((a) => a.id);

    if (selectedArtworkIds.length === 0) {
      ToastTopHelper.error(t('please_select_at_least_one_item'));
      return;
    }

    if (expiresAt && expiresAt.isBefore(moment())) {
      ToastTopHelper.error(t('expiration_date_must_be_in_future'));
      return;
    }

    if (tabValue === 'invite' && (!selectedProfiles || selectedProfiles.length === 0)) {
      ToastTopHelper.error(t('please_select_at_least_one_user'));
      return;
    }

    const isInviteTab = tabValue === 'invite';
    const recipientType = isInviteTab ? 'specific' : 'public';

    let banner = bannerImageKey;
    if (bannerFile && !banner) {
      const imageKey = generateImageKey(bannerFile);
      const uploadResp = await uploadImage({ imageKey, uploadFile: bannerFile });
      if (!uploadResp?.success) {
        ToastTopHelper?.error?.(t('upload_file_failed'));
        return;
      }
      banner = imageKey;
      setBannerImageKey(imageKey);
    }

    const formData = {
      title: title.trim(),
      description: '',
      artwork: selectedArtworkIds,
      share_type: 'artwork',
      expiration_type: expiresAt ? 'custom' : 'never',
      expires_at: expiresAt ? expiresAt.toISOString() : null,
      recipient_type: recipientType,
      recipient_user_ids: isInviteTab ? selectedProfiles.map((u) => u.id) : [],
      banner,
    };
    onNext(formData);
  };

  const handleClose = () => {
    setTitle('');
    setExpiresAt(null);
    setInputValue('');
    setSelectedProfiles([]);
    setBannerFile(null);
    setBannerImageKey('');
    setIsOpenArtworkSelect(false);
    setLocalSelectedItems({ artworks: [], exhibitions: [] });
    setDraftSelectedIds([]);
    setTabValue('invite');
    setSearchParams({});
    onClose();
  };

  useEffect(() => {
    if (open) {
      setLocalSelectedItems(selectedItems);
    } else {
      setLocalSelectedItems({ artworks: [], exhibitions: [] });
      setDraftSelectedIds([]);
      setTitle('');
      setExpiresAt(null);
      setInputValue('');
      setSelectedProfiles([]);
      setBannerFile(null);
      setBannerImageKey('');
      setTabValue('invite');
    }
  }, [open, selectedItems]);

  useEffect(() => {
    if (!bannerFile) {
      setBannerPreviewUrl('');
      return undefined;
    }

    const url = URL.createObjectURL(bannerFile);
    setBannerPreviewUrl(url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [bannerFile]);

  const selectedArtworksCount = localSelectedItems?.artworks?.length || 0;

  const draftSelectedArtworks = draftSelectedIds
    .map((id) => allSelectableArtworks.find((a) => a.id === id))
    .filter(Boolean);

  const isShareDisabled = !title.trim() || selectedArtworksCount === 0;

  const renderShareForm = () => (
    <Box className={classes.tabPanel}>
      <Box className={classes.section}>
        <Text className={classes.sectionTitle}>
          {t('select_artworks')}
          <span className={classes.requiredMark}> *</span>
        </Text>
        <Text className={classes.sectionDesc}>{t('select_artworks_description')}</Text>
        <Box className={classes.sectionActionRow}>
          {selectedArtworksCount === 0 && (
            <button
              type="button"
              className={classes.outlineButton}
              onClick={openSelector}
            >
              <AddOutlinedIcon className={classes.outlineButtonIcon} />
              {t('select')}
            </button>
          )}
        </Box>

        {selectedArtworksCount > 0 && (
          <Box
            role="button"
            tabIndex={0}
            onClick={openSelector}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') openSelector();
            }}
            className={classes.selectedSummaryRow}
          >
            <Text className={classes.selectedSummaryText}>
              {t('selected_count', { count: selectedArtworksCount }) ||
                `${selectedArtworksCount} artworks selected`}
            </Text>
            <KeyboardArrowRightOutlinedIcon className={classes.selectedSummaryIcon} />
          </Box>
        )}
      </Box>

      <Box className={classes.divider} />

      <Box className={classes.section}>
        <Text className={classes.sectionTitle}>{t('add_banner')}</Text>
        <Text className={classes.sectionDesc}>{t('add_banner_description')}</Text>
        <input
          ref={bannerInputRef}
          type="file"
          accept="image/*"
          className={classes.hiddenFileInput}
          onChange={(e) => {
            const file = e.target.files?.[0];
            setBannerFile(file || null);
          }}
        />

        {bannerPreviewUrl ? (
          <Box className={classes.bannerPreviewWrap}>
            <img
              src={bannerPreviewUrl}
              alt=""
              className={classes.bannerPreviewImg}
            />
          </Box>
        ) : (
          <Box className={classes.sectionActionRow}>
            <button
              type="button"
              className={classes.outlineButton}
              onClick={() => bannerInputRef.current?.click()}
            >
              <FileUploadOutlinedIcon className={classes.outlineButtonIcon} />
              {t('upload')}
            </button>
          </Box>
        )}

        <Text className={classes.helperText}>{t('banner_recommended_size')}</Text>
        {bannerPreviewUrl && (
          <button
            type="button"
            className={classes.replaceImageButton}
            onClick={() => bannerInputRef.current?.click()}
          >
            <Text className={classes.replaceImageText}>{t('replace_image')}</Text>
          </button>
        )}
      </Box>

      <Box className={classes.divider} />

      <Box className={classes.section}>
        <Text className={classes.sectionTitle}>{t('other_info')}</Text>
        <Text className={classes.sectionDesc}>{t('other_info_description')}</Text>

        <Box className={classes.twoCols}>
          <Box className={classes.col}>
            <Text className={classes.fieldLabel}>
              {t('memorable_name')}
              <span className={classes.requiredMark}> *</span>
            </Text>
            <TextField
              placeholder={t('enter')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
            />
          </Box>

          <Box className={classes.col}>
            <Text className={classes.fieldLabel}>{t('expiry_date_optional')}</Text>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DateTimePicker
                value={expiresAt}
                onChange={setExpiresAt}
                minDateTime={moment()}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={t('set_date')}
                    fullWidth
                  />
                )}
              />
            </LocalizationProvider>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <DialogCustom
      isShowDialog={open}
      onCancel={handleClose}
      onSubmit={handleNext}
      dialogTitle={t('share_selected_artworks')}
      textSubmit={t('share')}
      disabledButton={isShareDisabled}
      maxWidth="md"
      loading={loading}
      typeDialogTitle="lg"
      dialogHeaderStyle={classes.dialogHeader}
      dialogContentStyle={classes.dialogContent}
      dialogActionStyle={classes.dialogActions}
      submitButtonStyle={classes.submitButton}
      PaperProps={{ className: classes.paper }}
    >
      <Box className={classes.container}>
        <Tabs
          value={tabValue}
          onChange={(_, v) => setTabValue(v)}
          className={classes.tabs}
        >
          <Tab
            value="invite"
            label={t('invite_user')}
            className={classes.tab}
          />
          <Tab
            value="create"
            label={t('create_share_link')}
            className={classes.tab}
          />
        </Tabs>

        {tabValue === 'invite' && (
          <Box className={classes.inviteTop}>
            <Box className={classes.searchSection}>
              <Box className={classes.searchSectionContent}>
                <Autocomplete
                  multiple
                  options={userOptions}
                  filterOptions={(x) => x}
                  value={selectedProfiles}
                  onChange={handleSelectedChange}
                  inputValue={inputValue}
                  onInputChange={(_, value) => setInputValue(value)}
                  getOptionLabel={(option) => {
                    if (option.legal_name && option.name) {
                      return `${option.legal_name} [${option.name}]`;
                    }
                    return option.name;
                  }}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  className={classes.searchBox}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      const tagProps = getTagProps({ index });
                      const label =
                        option.legal_name && option.name
                          ? `${option.legal_name} [${option.name}]`
                          : option.name;

                      return (
                        <Box
                          key={tagProps.key}
                          className={classes.selectedUserTag}
                        >
                          <Avatar
                            className={classes.selectedUserTagAvatar}
                            src={option?.avatar || option?.profile_image || option?.image || ''}
                          >
                            {(option?.name || option?.legal_name || '').slice(0, 1)}
                          </Avatar>
                          <Box className={classes.selectedUserTagLabel}>{label}</Box>
                          <IconButton
                            className={classes.selectedUserTagRemove}
                            onClick={tagProps.onDelete}
                            size="small"
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      );
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={t('search')}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <Box className={classes.searchEndAdornment}>
                            <SearchIcon />
                          </Box>
                        ),
                      }}
                    />
                  )}
                />
              </Box>
            </Box>

            <Box className={classes.divider} />

            {/* People with Access Section */}
          </Box>
        )}

        {renderShareForm()}

        <DialogSelectArtworks
          isShowDialog={isOpenArtworkSelect}
          dialogTitle={t('select_artworks')}
          selectedArtworks={draftSelectedArtworks}
          artworks={artworksData}
          onCancel={closeSelector}
          onSubmit={applySelector}
          onScroll={handleScroll}
          onClickSelectArtworkItem={(artwork) => () => {
            setDraftSelectedIds((prev) => {
              if (prev.includes(artwork.id)) return prev.filter((x) => x !== artwork.id);
              return [...prev, artwork.id];
            });
          }}
        />
      </Box>
    </DialogCustom>
  );
};

export default ShareLinkSettingsDialog;
